import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + "/api" });

function fmtSlot(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-GT", { weekday: "long", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const kindLabel = (k) => (k === "interview" ? "Entrevista" : k === "induction" ? "Capacitación" : "Sesión");

export default function PublicScheduler() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [slots, setSlots] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const i = await API.get("/public-booking");
        setInfo(i.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "El link de agendamiento no está disponible.");
      }
    })();
  }, []);

  const goToSlots = async () => {
    if (!name.trim() || phone.replace(/\D/g, "").length < 7) {
      toast.error("Escribe tu nombre y un WhatsApp válido.");
      return;
    }
    setBusy(true);
    try {
      const s = await API.get("/public-booking/slots");
      setSlots(s.data || []);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar los horarios.");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!chosen) return;
    setBusy(true);
    try {
      const r = await API.post("/public-booking", { name: name.trim(), phone: phone.trim(), scheduled_at: chosen });
      setDone(r.data);
      toast.success("¡Confirmado!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error agendando");
    } finally { setBusy(false); }
  };

  if (error) return <Shell><div className="text-signal text-center py-6" data-testid="scheduler-error">{error}</div></Shell>;
  if (!info) return <Shell><div className="text-carbon-500 text-center py-6">Cargando…</div></Shell>;

  if (done) return (
    <Shell>
      <div className="text-center py-6">
        <div className="text-3xl mb-3">✓</div>
        <h2 className="font-heading text-xl mb-2">¡Listo, {name.split(" ")[0]}!</h2>
        <p className="text-sm text-carbon-600 mb-4">Tu {kindLabel(info.kind).toLowerCase()} quedó agendada para:</p>
        <div className="font-mono text-base bg-page border border-carbon-200 rounded py-3 px-4 inline-block capitalize">
          {fmtSlot(done.scheduled_at)}
        </div>
        <p className="mt-4 text-xs text-carbon-500">Te enviaremos los detalles por WhatsApp. 🙌</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div className="mb-5">
        <div className="text-xs uppercase tracking-wider text-carbon-500 mb-1">{kindLabel(info.kind)} · {info.duration_min} min</div>
        <h1 className="font-heading text-2xl">{info.title || "Agenda tu sesión"}</h1>
        <p className="text-sm text-carbon-600 mt-1">
          {step === 1 ? "Déjanos tus datos para reservar tu horario." : "Elegí el horario que mejor te quede."}
        </p>
      </div>

      {step === 1 && (
        <div className="space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">Nombre completo</span>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="scheduler-name"
              placeholder="Tu nombre" className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wide text-carbon-500">WhatsApp (con código de país)</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="scheduler-phone"
              placeholder="+502 5555 5555" className="w-full bg-page border border-carbon-200 rounded px-3 py-2 mt-1 text-sm font-mono" />
          </label>
          <div className="flex justify-end pt-2">
            <button onClick={goToSlots} disabled={busy} data-testid="scheduler-continue"
              className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-50">
              {busy ? "Cargando…" : "Ver horarios"}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[420px] overflow-y-auto scrollbar-thin pr-1">
            {slots.map((s) => (
              <button key={s.iso} onClick={() => setChosen(s.iso)} data-testid={`slot-${s.iso}`}
                className={`border rounded px-3 py-2.5 text-sm text-left transition-colors ${chosen === s.iso ? "border-terracotta bg-terracotta/10 text-terracotta" : "border-carbon-200 hover:border-terracotta"}`}>
                <div className="font-mono text-xs">{s.label_utc}</div>
                <div className="text-xs text-carbon-500 capitalize">{fmtSlot(s.iso)}</div>
              </button>
            ))}
            {slots.length === 0 && <div className="text-sm text-carbon-400 col-span-full text-center py-6">Sin horarios disponibles por ahora. Intenta más tarde.</div>}
          </div>
          <div className="mt-5 flex items-center justify-between gap-2">
            <button onClick={() => setStep(1)} className="text-sm text-carbon-500 hover:text-carbon-700">← Volver</button>
            <button onClick={submit} disabled={!chosen || busy} data-testid="scheduler-confirm"
              className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-50">
              {busy ? "Confirmando…" : "Confirmar horario"}
            </button>
          </div>
        </>
      )}
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-page flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      <div className="bg-surface border border-carbon-200 rounded-lg max-w-2xl w-full p-7 shadow-sm">
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-carbon-100">
          <div className="w-9 h-9 bg-terracotta rounded flex items-center justify-center font-heading font-bold text-white">HM</div>
          <div>
            <div className="font-heading text-base">Heroes Migrantes</div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-carbon-500">Community Engine</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
